import type { InstanceScopedNamespacePath } from "building-types/components/instanceScopedNamespacePathTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { DatumEntity } from "./datumEntityTypes";
import {
  isWallVerticalDatum,
  isFoundationVerticalDatum,
  isLevelDatum,
  isWallHorizontalDatum,
  isWallPlaneHorizontalDatum,
  isFoundationLateralDatum,
  DatumDirection,
  isLevelPlacementDatum,
  isAbsoluteVerticalDatum,
} from "./datumEntityTypes";

export enum DatumVerticalUsageType {
  WALL_VERTICAL = "WALL_VERTICAL",
  FOUNDATION_VERTICAL = "FOUNDATION_VERTICAL",
  LEVEL = "LEVEL",
  LEVEL_PLACEMENT = "LEVEL_PLACEMENT",
  COMPONENT_PLACEMENT = "COMPONENT_PLACEMENT",
  CEILING = "CEILING",
}

export enum DatumHorizontalUsageType {
  WALL_HORIZONTAL = "WALL_HORIZONTAL",
  WALL_PLANE_HORIZONTAL = "WALL_PLANE_HORIZONTAL",
}

export enum DatumLateralUsageType {
  FOUNDATION_LATERAL = "FOUNDATION_LATERAL",
}

export type DatumUsageType = DatumVerticalUsageType | DatumHorizontalUsageType | DatumLateralUsageType;

export const datumUsageTypeToDatumDirectionType = {
  WALL_VERTICAL: DatumDirection.VERTICAL,
  WALL_HORIZONTAL: DatumDirection.HORIZONTAL,
  WALL_PLANE_HORIZONTAL: DatumDirection.HORIZONTAL,
  FOUNDATION_VERTICAL: DatumDirection.VERTICAL,
  FOUNDATION_LATERAL: DatumDirection.LATERAL,
  LEVEL: DatumDirection.VERTICAL,
  LEVEL_PLACEMENT: DatumDirection.VERTICAL,
  COMPONENT_PLACEMENT: DatumDirection.VERTICAL,
  CEILING: DatumDirection.VERTICAL,
} as const satisfies Record<DatumUsageType, DatumDirection>;

export const datumUsageTypeToPredicate = {
  [DatumVerticalUsageType.LEVEL_PLACEMENT]: isLevelPlacementDatum,
  [DatumVerticalUsageType.WALL_VERTICAL]: isWallVerticalDatum,
  [DatumVerticalUsageType.FOUNDATION_VERTICAL]: isFoundationVerticalDatum,
  [DatumVerticalUsageType.LEVEL]: isLevelDatum,
  [DatumHorizontalUsageType.WALL_HORIZONTAL]: isWallHorizontalDatum,
  [DatumHorizontalUsageType.WALL_PLANE_HORIZONTAL]: isWallPlaneHorizontalDatum,
  [DatumLateralUsageType.FOUNDATION_LATERAL]: isFoundationLateralDatum,
  [DatumVerticalUsageType.COMPONENT_PLACEMENT]: isAbsoluteVerticalDatum,
  [DatumVerticalUsageType.CEILING]: isLevelDatum,
} as const satisfies Record<DatumUsageType, (datum: DatumEntity) => boolean>;

export interface DatumUsageBase extends InstanceScopedNamespacePath<"DATUM"> {
  readonly offset?: number;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export type DatumUsage<T extends DatumUsageType = DatumUsageType> = DatumUsageBase & { readonly __usage: T };

export function datumUsageNew<T extends DatumUsageType>(
  _usageType: T,
  entityId: string,
  offset?: number,
  componentInstanceChain?: ForeignEntityId<"COMPONENT_INSTANCE">[],
): DatumUsage<T> {
  return { __usage: _usageType, entityId, offset, componentInstanceChain };
}
